import type React from "react";
import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import invariant from "ts-invariant";

import { useCreateUserCourseTemplate } from "api/hooks/useCreateUserCourseTemplate";
import { useGetUserCourseTemplateQuery } from "api/hooks/useGetUserCourseTemplate";
import { GET_USER_COURSE_TEMPLATES_QUERY_KEY } from "api/hooks/useGetUserCourseTemplates";
import { useUpdateUserCourseTemplate } from "api/hooks/useUpdateUserCourseTemplate";
import type { FullUserCourse } from "api/schemas/UserCourse";
import { useProfileContext } from "contexts/ProfileContext";
import { UserCourseEditor } from "routes/patients/PatientProfile/components/UserCourse/components/UserCourseEditor";
import { convertTemplateToUserCourse } from "routes/patients/PatientProfile/components/UserCourse/utils/conversion";
import { Notification } from "shared/atoms/Notification";

import { ContentContainer } from "../../components/styles";

export const TemplateForm: React.FC = () => {
  const { id: selectedTemplateId } = useParams();

  const queryClient = useQueryClient();
  const { profile } = useProfileContext();
  invariant(profile);
  const therapistId = profile.id;
  const navigate = useNavigate();
  const [courseData, setCourseData] = useState<FullUserCourse | undefined>(undefined);
  const [name, setName] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { t } = useTranslation();

  const { mutateAsync: createTemplateMutate, error: createTemplateIsError } = useCreateUserCourseTemplate({
    onSuccess: () => {
      queryClient.invalidateQueries(GET_USER_COURSE_TEMPLATES_QUERY_KEY);
    },
  });

  const { mutateAsync: updateTemplateMutate, error: updateTemplateIsError } = useUpdateUserCourseTemplate({
    onSuccess: () => {
      queryClient.invalidateQueries(GET_USER_COURSE_TEMPLATES_QUERY_KEY);
    },
  });

  const { error: getTemplateIsError, refetch: refetchTemplateData } = useGetUserCourseTemplateQuery(
    { templateId: parseInt(selectedTemplateId as string, 10) },
    {
      enabled: false,

      onSuccess: data => {
        if (data.standardized) {
          setErrorMessage(t("usercourse.edit_not_allowed"));
          return;
        }

        setCourseData(convertTemplateToUserCourse(data));

        setName(data.name || "");
      },
    }
  );

  useEffect(() => {
    if (selectedTemplateId) {
      refetchTemplateData();
    } else {
      setCourseData(undefined);
      setName("");
    }
  }, [selectedTemplateId]);

  const hasError = createTemplateIsError || updateTemplateIsError || getTemplateIsError || errorMessage !== undefined;

  if (hasError) {
    return (
      <div>
        <Notification variant="danger" style={{ marginBottom: "5px" }}>
          {errorMessage || t("errors.generic")}
        </Notification>
      </div>
    );
  }

  return (
    <ContentContainer>
      <UserCourseEditor
        showActiveSelector={false}
        currentProtocolWeek={1}
        showNameInput
        initialData={courseData}
        initialName={name}
        saveButtonLabel={t("usercourse.templates.save_as.cta")}
        canEditLessons
        onSaveUserCourse={async (course, _name) => {
          if (selectedTemplateId) {
            await updateTemplateMutate(
              {
                therapistId,
                templateId: parseInt(selectedTemplateId, 10),
                course: { ...course, name: _name },
              },
              {
                onSuccess: () => {
                  queryClient.invalidateQueries(GET_USER_COURSE_TEMPLATES_QUERY_KEY);
                  navigate(`/library/your-library/templates/${selectedTemplateId}`);
                },
              }
            );
          } else {
            await createTemplateMutate(
              {
                therapistId,
                course: { ...course, name: _name },
              },
              {
                onSuccess: res => {
                  navigate(`/library/your-library/templates/${res.id}`);
                },
              }
            );
          }
        }}
      />
    </ContentContainer>
  );
};
